// Story 1.20 — admin-mediated rectification form (POST /rectification-request).
//
// The user provides a free-text reason + a JSON-pasted requested_changes
// object. v1 lean: just a textarea where the user pastes JSON; v2 can
// add a structured form once the rectification surface sees real use.

import { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@greenhold/components";
import toast from "react-hot-toast";

import { useRequestRectification } from "./useRequestRectification";

export function RectificationForm() {
  const [reason, setReason] = useState("");
  const [changesJson, setChangesJson] = useState(
    '{\n  "display_name": "Corrected Name"\n}',
  );
  const mutate = useRequestRectification();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim().length === 0) {
      toast.error("Reason is required.");
      return;
    }
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(changesJson) as Record<string, unknown>;
    } catch (err) {
      toast.error("Requested changes is not valid JSON.");
      return;
    }
    parsed.reason = reason;
    mutate.mutate(
      { body: { requested_changes: parsed } },
      {
        onSuccess: () => {
          toast.success(
            "Rectification request submitted. We'll respond within 30 days.",
          );
          setReason("");
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Request admin rectification (Article 16)</CardTitle>
        <CardDescription>
          For corrections that aren't covered by self-service (e.g., name
          on issued certificates, financial-row corrections). We respond within
          30 days per our GDPR SLA.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="rectification-reason"
              className="block text-sm font-medium mb-1"
            >
              Reason (required, ≤ 500 chars)
            </label>
            <textarea
              id="rectification-reason"
              className="w-full border rounded-md p-2 text-sm font-body"
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Briefly explain what needs correcting and why"
              required
            />
          </div>
          <div>
            <label
              htmlFor="rectification-changes"
              className="block text-sm font-medium mb-1"
            >
              Requested changes (JSON)
            </label>
            <textarea
              id="rectification-changes"
              className="w-full border rounded-md p-2 text-sm font-mono"
              rows={6}
              value={changesJson}
              onChange={(e) => setChangesJson(e.target.value)}
              placeholder='{"display_name": "Corrected Name"}'
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            disabled={mutate.isPending}
          >
            {mutate.isPending ? "Submitting…" : "Submit rectification request"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
